// Type-only imports: the SDK is passed in by the caller, so this module adds no
// runtime dependency on it and tests can hand it a fake.
import type { datadogRum, RumInitConfiguration } from "@datadog/browser-rum";

type RumSdk = Pick<typeof datadogRum, "init" | "getInitConfiguration">;

/** Init options every app sends unless it overrides them. */
const RUM_DEFAULTS = {
  sessionSampleRate: 100,
  trackResources: true,
  trackUserInteractions: true,
  trackLongTasks: true,
} satisfies Partial<RumInitConfiguration>;

export type RumConfig = Omit<
  RumInitConfiguration,
  "applicationId" | "clientToken"
> & {
  /** RUM stays off when the application id or the client token is missing. */
  applicationId: string | undefined;
  clientToken: string | undefined;
};

/**
 * Initializes Datadog RUM once per page load with the shared defaults merged
 * with the app config. Returns whether RUM is initialized, newly or already;
 * `false` (and nothing happens) when the credentials are missing.
 */
export function initRum(
  sdk: RumSdk,
  { applicationId, clientToken, ...options }: RumConfig
): boolean {
  if (!applicationId || !clientToken) return false;
  if (sdk.getInitConfiguration()) return true;

  sdk.init({ applicationId, clientToken, ...RUM_DEFAULTS, ...options });
  return true;
}
